import { useState } from 'react';
import { toast } from 'sonner';
import JSZip from 'jszip';
import { Menu, Music, Mic, Volume2, VolumeX, Play, Pause, Headphones, MoreHorizontal, Download, Drum, Loader2 } from 'lucide-react';
import AudioUploader from '@/components/AudioUploader';
import { SessionHeader } from '@/components/session/SessionHeader';
import { ProcessingStatus } from '@/components/session/ProcessingStatus';
import { StemsList } from '@/components/session/StemsList';
import WaveformDisplay from '@/components/WaveformDisplay';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSidebar } from '@/components/ui/sidebar';
import { useMultitrack } from '@/components/multitrack/useMultitrack';
import { parseSongInfo } from '@/utils/songUtils';
import type { Stem } from '@/types/stem';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface SessionContentProps {
  session: any;
  isProcessing: boolean;
  progress: number;
  processingError: string | null;
  processingDuration: number;
  onRetry: (sessionId: string) => void;
  onDelete: (sessionId: string) => void;
  onFileSelect: (file: File, type: 'stem' | 'acapella' | 'drum') => void;
  sessionType: 'stem' | 'acapella' | 'drum';
}

export const SessionContent = ({
  session,
  isProcessing,
  progress,
  processingError,
  processingDuration,
  onRetry,
  onDelete,
  onFileSelect,
  sessionType,
}: SessionContentProps) => {
  const isMobile = useIsMobile();
  const { toggleSidebar } = useSidebar();
  const [showMultitrack, setShowMultitrack] = useState(true);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (stem: Stem) => {
    try {
      setIsDownloading(true);
      toast.info(`Downloading ${stem.name}...`);
      
      const response = await fetch(stem.url);
      const blob = await response.blob();

      const urlParts = stem.url.split('?')[0].split('.');
      const extension = urlParts[urlParts.length - 1]; // Extract the file extension
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${stem.name.toLowerCase()}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Downloaded ${stem.name} successfully`);
    } catch (error) {
      console.error('Error downloading stem:', error);
      toast.error(`Failed to download ${stem.name}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadAll = async () => {
    if (!session?.stems) return;

    try {
      setIsDownloading(true);
      toast.info('Your stems are downloading... they may take a while');
      
      const downloadPromises = (session.stems as Stem[]).map(async (stem) => {
        const response = await fetch(stem.url);
        const blob = await response.blob();
        const urlParts = stem.url.split('?')[0].split('.');
        const extension = urlParts[urlParts.length - 1]; // Extract the file extension
        return {
          name: `${stem.name.toLowerCase()}.${extension}`,
          blob
        };
      });

      const downloads = await Promise.all(downloadPromises);
      const zip = new JSZip();
      downloads.forEach(({ name, blob }) => {
        zip.file(name, blob);
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const downloadUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${session.original_filename.replace(/\.[^/.]+$/, '')}_stems.zip`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('All stems downloaded successfully');
    } catch (error) {
      console.error('Error downloading all stems:', error);
      toast.error('Failed to download stems');
    } finally {
      setIsDownloading(false);
    }
  };

  const {
    containerRef,
    isPlaying,
    tracks,
    masterVolume,
    togglePlayPause,
    setTrackVolume,
    setTrackMute,
    setTrackSolo,
    setGlobalVolume,
    loadProgress
  } = useMultitrack(
    session?.stems as Stem[] || [],
    session?.original_audio_url || ''
  );

  const handleDawClick = () => {
    const songInfo = parseSongInfo(session?.original_filename || '');
    posthog.capture('DAW CTA Clicked', {
      songTitle: songInfo.title,
      artist: songInfo.artist
    });
    window.open('http://sesh.fm/product-tour', '_blank');
  };

  if (!session) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex items-center gap-2 mb-4">
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10"
              onClick={toggleSidebar}
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              {sessionType === 'stem' ? (
                <Music className="h-6 w-6 text-white" />
              ) : sessionType === 'acapella' ? (
                <Mic className="h-6 w-6 text-white" />
              ) : (
                <Drum className="h-6 w-6 text-white" />
              )}
              <h1 className="text-2xl font-semibold tracking-tight text-white truncate max-w-[22ch]">
                {sessionType === 'stem' 
                  ? 'AI Stem Splitter' 
                  : sessionType === 'acapella'
                  ? 'Acapella Extractor'
                  : 'Drum Remover'
                }
              </h1>
            </div>
            <p className="text-sm text-gray-400">
              {sessionType === 'stem' 
                ? 'Upload audio to split stems'
                : sessionType === 'acapella'
                ? 'Upload audio to extract acapellas'
                : 'Upload music files and extract the drum tracks in one click'
              }
            </p>
          </div>
        </div>
        <AudioUploader onFileSelect={(file) => onFileSelect(file, sessionType)} />
        {isProcessing && (
          <ProcessingStatus
            isProcessing={isProcessing}
            progress={progress}
            error={null}
            onRetry={() => {}}
            processingDuration={processingDuration}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 p-4 md:p-8 w-full overflow-x-hidden max-h-screen">
      <SessionHeader
        filename={session?.original_filename}
        sessionId={session?.id}
        onDelete={() => session?.id && onDelete(session.id)}
        onDownloadAll={handleDownloadAll}
        showDownload={session?.status === 'completed' && session?.stems && (session.stems as Stem[]).length > 0}
        isDownloading={isDownloading}
      />

      {session.status === 'processing' && (
        <div className="space-y-4 md:space-y-6 max-w-full">
          <div className="bg-card rounded-lg max-w-full">
            <WaveformDisplay
              audioUrl={session.original_audio_url}
              name="Original"
              index={0}
            />
          </div>
          <ProcessingStatus
            isProcessing={true}
            progress={progress}
            error={null}
            onRetry={() => {}}
            processingDuration={processingDuration}
          />
        </div>
      )}

      {session.status === 'failed' && (
        <ProcessingStatus
          isProcessing={isProcessing}
          progress={progress}
          error="There was an error processing your audio file."
          onRetry={() => onRetry(session.id)}
          processingDuration={processingDuration}
        />
      )}

      {processingError && (
        <ProcessingStatus
          isProcessing={isProcessing}
          progress={progress}
          error={processingError}
          onRetry={() => onRetry(session.id)}
          processingDuration={processingDuration}
        />
      )}

      {session?.stems && session.status === 'completed' && (session.stems as Stem[]).length > 0 && (
        <div className="space-y-4 w-full">
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between w-full">
              <div className="flex items-center gap-6">
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={isPlaying ? "default" : "outline"}
                        size="icon"
                        onClick={togglePlayPause}
                        className={`h-12 w-12 rounded-full ${isPlaying ? "bg-[#3B82F6] hover:bg-[#60A5FA] text-white" : "bg-white hover:bg-white/90"}`}
                      >
                        {isPlaying ? (
                          <Pause className="h-6 w-6 text-white" />
                        ) : (
                          <Play className="h-6 w-6 text-black" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isPlaying ? "Pause stems" : "Play stems"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-muted-foreground">Master</span>
                  <div className="flex items-center gap-2 w-32 md:w-48">
                    <VolumeX className="h-4 w-4" />
                    <Slider
                      value={[masterVolume * 100]}
                      onValueChange={(value) => setGlobalVolume(value[0] / 100)}
                      max={100}
                      step={1}
                      className="[&_.relative.h-1]:h-[3px]"
                    />
                    <Volume2 className="h-4 w-4" />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {session.status === 'completed' && session?.stems && (session.stems as Stem[]).length > 0 && (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          onClick={handleDownloadAll} 
                          variant="outline" 
                          className="h-10 px-4 flex items-center gap-2 flex-1 md:flex-none"
                          disabled={isDownloading}
                        >
                          {isDownloading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          <span className="md:hidden">Download</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Download all stems
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="default" className="bg-[#3B82F6] hover:bg-[#60A5FA] text-white h-10 px-4 flex-1 md:flex-none" onClick={handleDawClick}>
                        <img src="/lovable-uploads/1f60214c-efc7-49df-9a80-67c09b9772bf.png" alt="Sesh Logo" className="w-4 h-4 mr-2" />
                        <span className="hidden md:inline">Remix in Sesh</span>
                        <span className="md:hidden">Remix in Sesh</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Edit in our browser DAW
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <div className="w-full overflow-x-scroll" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <div className="flex max-w-full relative" style={{ overflow: 'visible' }}>
                <div className="w-fit flex-shrink-0 flex flex-col gap-[1px] rounded-lg overflow-hidden z-30">
                  {tracks.map(track => (
                    <div 
                      key={track.id} 
                      className="flex items-center gap-2 px-2 bg-card w-fit flex-shrink-0" 
                      style={{ 
                        height: '49px',
                        lineHeight: '1'
                      }}
                    >
                      <span className="w-24 text-sm truncate leading-none">{track.name}</span>
                      
                      <div className="flex items-center gap-2 w-20 h-full">
                        <Slider
                          value={[track.volume * 100]}
                          onValueChange={(value) => setTrackVolume(track.id, value[0] / 100)}
                          max={100}
                          step={1}
                          className="[&_.relative.h-1]:h-[3px]"
                        />
                      </div>

                      <div className="flex items-center gap-1 h-full">
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant={track.isMuted ? "destructive" : "ghost"}
                                size="icon"
                                onClick={() => setTrackMute(track.id)}
                                className="h-8 w-8 flex items-center justify-center"
                              >
                                <VolumeX className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Mute track
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant={track.isSoloed ? "default" : "ghost"}
                                size="icon"
                                onClick={() => setTrackSolo(track.id)}
                                className={`h-8 w-8 flex items-center justify-center ${track.isSoloed ? "bg-[#3B82F6] hover:bg-[#60A5FA] text-white" : ""}`}
                              >
                                <Headphones className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Solo track
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="relative flex-1 w-0">
                  <div 
                    className="absolute inset-0 rounded-lg opacity-25 z-0"
                    style={{
                      background: `linear-gradient(180deg, ${tracks.map(track => track.color).join(', ')})`,
                      height: `${tracks.length * 49 + (tracks.length - 1)}px`
                    }}
                  />
                  
                  <div 
                    id="loading-bars-container"
                    data-testid="loading-bars-container"
                    className="absolute inset-0 z-10 pointer-events-none flex flex-col gap-[1px]"
                    style={{
                      height: `${tracks.length * 49 + (tracks.length - 1)}px`
                    }}
                  >
                    {tracks.map((track) => (
                      <div
                        key={`loading-${track.id}`}
                        id={`loading-track-${track.id}`}
                        data-testid={`loading-track-${track.id}`}
                        style={{ height: '49px' }}
                        className="relative"
                      >
                        <div 
                          id={`loading-background-${track.id}`}
                          data-testid={`loading-background-${track.id}`}
                          className="absolute inset-0 bg-black/20"
                        >
                          <div 
                            id={`loading-bar-${track.id}`}
                            data-testid={`loading-bar-${track.id}`}
                            className={`absolute inset-0 transition-all duration-300 ease-out ${loadProgress[track.id] === 100 ? 'opacity-0' : ''}`}
                            style={{
                              width: `${loadProgress[track.id] || 0}%`,
                              backgroundColor: track.color,
                              opacity: 0.15,
                              transitionProperty: 'width, opacity',
                              transitionDuration: '300ms'
                            }}
                          >
                            <div 
                              id={`loading-pulse-${track.id}`}
                              data-testid={`loading-pulse-${track.id}`}
                              className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/20 to-transparent"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div 
                    ref={containerRef} 
                    className="relative rounded-lg overflow-hidden z-20" 
                    style={{ 
                      height: `${tracks.length * 49 + (tracks.length - 1)}px`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
